import React, { useEffect, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import api from "../lib/api";
import { PageHeader } from "../components/common/PageParts";
import { CryptoLogo, Sparkline } from "../components/common/CryptoBits";
import { fmtPrice, fmtPct, fmtCompact } from "../lib/format";
import { ScanLine, ArrowUpDown } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "LONG", label: "Long Bias" },
  { key: "SHORT", label: "Short Bias" },
  { key: "uptrend", label: "Uptrend" },
  { key: "downtrend", label: "Downtrend" },
];

export default function MarketScanner() {
  const { setActiveSymbol } = useDashboard();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("longScore");

  useEffect(() => {
    let m = true;
    const load = () => api.getOverview("1h").then((o) => m && setRows(o.rows.filter((r) => !r.error))).catch(() => {});
    load(); const t = setInterval(load, 30000);
    return () => { m = false; clearInterval(t); };
  }, []);

  const filtered = rows
    .filter((r) => filter === "all" ? true : r.direction === filter || r.regime === filter)
    .sort((a, b) => (b[sort] ?? 0) - (a[sort] ?? 0));

  return (
    <div className="ym-fade">
      <PageHeader title="Pemindai Pasar" subtitle="Live scan across favorites — scored on real technicals every 30s"
        right={<span className="pill" style={{ color: "var(--accent-soft)", background: "rgba(139,92,246,.12)", borderColor: "transparent" }}><ScanLine size={13} /> {rows.length} pairs</span>} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className="pill ym-hover"
            style={{ background: filter === f.key ? "rgba(139,92,246,.2)" : "var(--panel-2)", color: filter === f.key ? "#fff" : "var(--text-muted)", borderColor: "var(--border)" }}>{f.label}</button>
        ))}
        <button onClick={() => setSort((s) => s === "longScore" ? "shortScore" : "longScore")} className="pill ym-hover ml-auto"
          style={{ background: "var(--panel-2)", color: "var(--text-muted)", borderColor: "var(--border)" }}>
          <ArrowUpDown size={12} /> Sort: {sort === "longScore" ? "Long" : "Short"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((r) => (
          <div key={r.id} onClick={() => setActiveSymbol(r.symbol)} className="ym-card ym-hover p-4 cursor-pointer">
            <div className="flex items-center gap-2.5 mb-3">
              <CryptoLogo base={r.base} color={r.color} size={30} />
              <div className="leading-tight">
                <div className="font-bold text-[14px]">{r.base}/USDT</div>
                <div className="text-[10px]" style={{ color: "var(--text-dim)" }}>{r.provider}</div>
              </div>
              <span className="ml-auto pill" style={{ color: r.change24h >= 0 ? "var(--green)" : "var(--red)", background: r.change24h >= 0 ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", borderColor: "transparent" }}>{fmtPct(r.change24h)}</span>
            </div>
            <div className="flex items-end justify-between mb-3">
              <div className="text-[19px] font-bold tabnum">${fmtPrice(r.price)}</div>
              <Sparkline data={r.spark} color={r.change24h >= 0 ? "#22c55e" : "#ef4444"} width={90} height={30} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Mini label="Long" v={r.longScore} c="var(--green)" />
              <Mini label="Short" v={r.shortScore} c="var(--red)" />
              <Mini label="Vol" v={fmtCompact(r.volume24h)} c="var(--text-muted)" small />
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px]">
              <span className="capitalize" style={{ color: "var(--accent-soft)" }}>{r.regime}</span>
              <span className="pill" style={{ color: r.direction === "LONG" ? "var(--green)" : r.direction === "SHORT" ? "var(--red)" : "var(--text-muted)", background: "var(--panel-2)", borderColor: "var(--border)" }}>{r.direction}</span>
            </div>
          </div>
        ))}
      </div>
      {!rows.length && <div className="text-center py-10" style={{ color: "var(--text-dim)" }}>Scanning live markets…</div>}
    </div>
  );
}

function Mini({ label, v, c, small }) {
  return (
    <div className="rounded-lg px-2 py-1.5 text-center" style={{ background: "var(--panel-2)", border: "1px solid var(--border)" }}>
      <div className="text-[8px] font-semibold tracking-wide" style={{ color: "var(--text-dim)" }}>{label.toUpperCase()}</div>
      <div className={`${small ? "text-[11px]" : "text-[14px]"} font-bold tabnum`} style={{ color: c }}>{v ?? "—"}</div>
    </div>
  );
}
